import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setTokenGetter } from "@/lib/api";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext(null);
export const STAFF = ["owner", "moderator", "support"];
const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoaded = session !== undefined;
  const isSignedIn = !!session;
  const uid = session?.user?.id;
  setTokenGetter(isSignedIn ? getToken : null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async () => {
    if (!uid) { setMe(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      setError(null);
    } catch (e) {
      setMe(null);
      setError(e?.response?.data?.detail || "generic");
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (isLoaded) { setLoading(true); refresh(); }
  }, [isLoaded, uid, refresh]);

  const signInWithGoogle = (next = "/") => supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${window.location.origin}/sign-in?next=${encodeURIComponent(next)}` },
  });
  const signOut = async () => { await supabase.auth.signOut(); setMe(null); };

  const value = { me, setMe, refresh, error, isLoaded, isSignedIn, ready: isLoaded && !loading, signInWithGoogle, signOut,
    isStaff: !!me && STAFF.includes(me.role), isModerator: !!me && ["owner", "moderator"].includes(me.role) };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useMe = () => useContext(AuthContext);
